import { useCallback, useRef, useState } from "react";
import { Modal, Button, Tab, Loading } from "@etframework/ui.components";
import ReactDOM from "react-dom";
import useModalEvent from "../../hooks/useModalEvent";
import { useTranslation } from "react-i18next";
import { SelectUsers } from "../../components/AccessModalComponents/SelectUsers";
import { AccessModalGrid } from "../../components/AccessModalComponents/AccessModalGrid";
import { useUpdateBoardPermissions } from "../../data/mutations/boardMutations";
import { useUsers } from "../../data/queries/userQueries";
import { useQueryClient } from "@tanstack/react-query";
import { useBoardPermissions } from "../../data/queries/boardQueries";
import Enums from "../../types/enums";

export const AccessModal = () => {
  const [currentBoard, setCurrentBoard] = useState({});
  const { t, ready } = useTranslation(["AccessModal"], {
    useSuspense: false,
  });
  const [_showRestrictedSection] = useState(true);
  const { mutate: updateBoardPermissions, isPending } =
    useUpdateBoardPermissions();
  const { data: usersDataSource } = useUsers();

  const { data: permissionsData } = useBoardPermissions(currentBoard?.Id);

  const queryClient = useQueryClient();

  const selectUsersRef = useRef(null);
  const selectGroupsRef = useRef(null);
  const accessGridRef = useRef(null);

  const addUser = (users) => {
    const gridData = getData();
    let newGridData = [];

    users.forEach((user) => {
      const userAlreadyAdded = gridData.find(
        (userInGrid) => userInGrid.Id === user.Id
      );

      if (userAlreadyAdded) {
        // prevent duplicate users
        selectUsersRef.current.clear();
        return;
      }

      const userToAdd = {
        PermissionType: Enums.PermissionType.User,
        Id: user.Id,
        Name: user.FullName,
      };

      newGridData = [...newGridData, userToAdd];
    });

    newGridData = [...gridData, ...newGridData];

    setData(newGridData);
    selectUsersRef.current.setUsersInGrid(newGridData);
    selectUsersRef.current.clear();
  };

  const getData = useCallback(() => {
    if (_showRestrictedSection) {
      return accessGridRef.current?.getData();
    } else {
      return null;
    }
  }, [_showRestrictedSection]);

  const setData = useCallback(
    (data) => {
      if (_showRestrictedSection) {
        accessGridRef.current?.setData(data);
      }
    },
    [_showRestrictedSection]
  );

  const onSaveChanges = () => {
    const gridData = getData();

    if (
      checkIfHasChanges(
        accessGridRef.current?.getInitialData(),
        accessGridRef.current?.getGridCurrentData()
      )
    ) {
      const permissionsUpdated = {
        boardId: currentBoard?.Id,
        entities: gridData,
      };

      updateBoardPermissions(
        { permissions: permissionsUpdated },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([
              "get-board-permissions",
              currentBoard?.Id,
            ]);
          },
          onError: () => {},
        }
      );
    }
  };

  //Checking if two array of objects have the same Ids so it does not
  //update.
  const checkIfHasChanges = (array1, array2) => {
    //extracting both array's ids
    const ids1 = array1.map((item) => item.Id).sort((a, b) => a - b);
    const ids2 = array2.map((item) => item.Id).sort((a, b) => a - b);

    return JSON.stringify(ids1) !== JSON.stringify(ids2);
  };

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const onModelBeforeClose = () => {};

  const onModalClose = () => {
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
    modalRef?.current?.close();
  };

  const openModal = () => {
    setShowModal(true);
    modalRef?.current?.open();
  };

  const toggleModal = (event) => {
    setCurrentBoard(event.detail.currentBoard);
    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    return (
      <div className="et-access-modal">
        {isPending ? (
          <Loading text={t("Loading")} />
        ) : (
          <div>
            <div>
              <span className="access-note d-block">{t("Note")}</span>
              <span className="access-board-name d-block">
                {currentBoard?.Name}
              </span>
            </div>
            <div className="mt-4">
              {_showRestrictedSection ? (
                <div>
                  <div className="access-header">
                    <span>{t("Header")}</span>
                  </div>
                  <div>
                    <Tab
                      tabNames={[t("AddIndividually")]}
                      tabComponents={[
                        <SelectUsers
                          id={`${
                            ModalEvents.AccessModal.Id + "_add-users-tab"
                          }`}
                          ref={selectUsersRef}
                          key={"users-tab"}
                          click={addUser}
                          accessGridRef={accessGridRef}
                          usersDataSource={usersDataSource}
                        />,
                      ]}
                      typeOfTab="purchase"
                      reorderActiveTab={false}
                    />
                  </div>
                </div>
              ) : (
                <div></div>
              )}
              <div className="mt-3">
                <AccessModalGrid
                  ref={accessGridRef}
                  boardId={currentBoard?.Id}
                  selectUsersRef={selectUsersRef}
                  selectGroupsRef={selectGroupsRef}
                  permissionsData={permissionsData}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end access-modal-btns gap-3">
              <Button
                id={`${ModalEvents.AccessModal.Id + "_close-button"}`}
                text={t("Close")}
                className="btn-tertiary"
                onClick={toggleModal}
              />
              <Button
                id={`${ModalEvents.AccessModal.Id + "_save-changes-button"}`}
                text={t("SaveChanges")}
                className="btn btn-primary"
                onClick={onSaveChanges}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  useRegisterEvent(ModalEvents.AccessModal, toggleModal);

  if (!ready) return null;

  return ReactDOM.createPortal(
    <>
      <Modal
        id={ModalEvents.AccessModal.Id}
        ref={modalRef}
        isModal
        useSpinner
        showCloseIcon={true}
        visible={showModal}
        content={<ModalContent />}
        close={onModalClose}
        beforeClose={onModelBeforeClose}
        minHeight="20vh"
        width="800px"
      ></Modal>
    </>,
    document.body
  );
};
